import { DateTime } from "luxon"
import { useMemo, useState } from "react"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { Survey, TableRatingsReport } from "../surveyTypes"
import { fetchAllRatings } from "../surveyReportApi"
import { useSurveys } from "../hooks/useSurveys"
import { DropdownExportButtonRatings } from "./DropdownExportButtonRatings"
import { displaySmiley } from "./SmileySummaryCard"
import { transformData } from "./AllRatingsTab"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"

export function RatingCommentsTab() {
    const [survey, setSurvey] = useState<Survey | null>(null)
    const { startDate, endDate } = useInitialDateFilters()
    const [dateRange, setDateRange] = useState(() => ({
        startDate: startDate ?? DateTime.now().minus({ days: 14 }),
        endDate: endDate ?? DateTime.now(),
    }))

    const { l10n } = useAppLocalization()

    const dateTimeFactory = useDateTime()
    const { SelectSurvey } = useSurveys()

    const ratingsResult = useApiQuery(fetchAllRatings, {
        queryName: "survey-ratings-with-comments",
        dependencies: {
            surveyId: survey?.id ?? 0,
            startDate: dateRange.startDate.toISODate(),
            endDate: dateRange.endDate.toISODate(),
            hasComments: true,
        },
        select: transformData,
        enabled: Boolean(survey?.id),
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(ratingsResult)

    const columns = useMemo<MRT_ColumnDef<TableRatingsReport>[]>(
        () => [
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("all-rating-time"),
                    id: "created",
                },
                (row) => row.created,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("all-rating-question"),
                accessorKey: "question.title",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("all-rating-score"),
                id: "smiley",
                accessorFn: (originalRow) => originalRow.question.smiley.value,

                Cell: ({ row }) => {
                    return (
                        <>
                            <img src={displaySmiley(row.original.question.smiley.value)} height={50} />
                        </>
                    )
                },
            }),
            columnFactory.nameColumn({
                header: l10n.getString("all-rating-comment"),
                accessorKey: "question.smiley.comment",
            }),
        ],
        [l10n, dateTimeFactory],
    )

    return (
        <ApiQueryTable
            columns={columns}
            enableRowSelection
            initialState={{ density: "spacious" }}
            renderToolbarInternalActions={({ table }) => (
                <StandardTableFilter variant="right" table={table}>
                    <DropdownExportButtonRatings
                        rowsIsSelected={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        containsRows={table.getPrePaginationRowModel().rows.length === 0}
                        allRowsWithFilter={table.getPrePaginationRowModel().rows}
                        selectedRows={table.getSelectedRowModel().rows}
                        chosenStartDate={dateRange.startDate?.toISODate() ?? ""}
                        chosenEndDate={dateRange.endDate?.toISODate() ?? ""}
                    />
                </StandardTableFilter>
            )}
            renderTopToolbarCustomActions={() => (
                <StandardTableFilter variant="left">
                    <SelectSurvey variant="outlined" size="medium" survey={survey} setSurvey={setSurvey} />
                    <StandardDateRangeFilter value={dateRange} onChange={setDateRange} />
                </StandardTableFilter>
            )}
        />
    )
}
